"use client";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Combobox, HStack, Portal, Text, useFilter, useListCollection, } from "@chakra-ui/react";
import { getCountries } from "libphonenumber-js";
import React, { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import ReactCountryFlag from "react-country-flag";
import { COUNTRY_NAMES } from "./country-names";
/**
 * The country's name in the reader's language.
 *
 * Product-language names are pinned so different server/browser ICU versions
 * cannot change the initial rendered rows. Other locales fall back to English.
 */
const getCountryName = (code, locale) => {
    const language = locale.toLowerCase().split("-")[0];
    const names = COUNTRY_NAMES[language === "fr" || language === "it" ? language : "en"];
    return names[code] ?? COUNTRY_NAMES.en[code] ?? code;
};
/** Kept as the default so every existing caller reads exactly as before. */
const DEFAULT_COUNTRY_LOCALE = "en";
/**
 * The country list never asks for more room than the screen has: 320px wide
 * at most but never past the viewport (a 360px phone), 320px tall at most but
 * never past the space `fitViewport` measures on the side it opens.
 */
export const COUNTRY_LIST_MIN_WIDTH = "min(320px, calc(100vw - 32px))";
export const COUNTRY_LIST_MAX_HEIGHT = "min(320px, var(--available-height, 320px))";
// Priority countries to show at the top
const PRIORITY_COUNTRIES = [
    "FR",
    "US",
    "GB",
    "CA",
    "DE",
    "ES",
    "IT",
    "NL",
    "BE",
    "PT",
    "IE",
    "AT",
];
/**
 * The rows the picker shows: priority countries first, then the rest in the
 * reader's own alphabetical order, optionally narrowed to an allowed set.
 *
 * Exported because the ordering is the part worth locking down: localizing the
 * labels without localizing the collation leaves a French list sorted on
 * English names, which reads as no order at all. A country absent from
 * `libphonenumber-js` metadata is absent here too, allowed set or not.
 */
export const getCountryOptions = (locale = DEFAULT_COUNTRY_LOCALE, allowedCountries) => {
    const all = getCountryData(locale);
    if (!allowedCountries || allowedCountries.length === 0)
        return all;
    const allowedSet = new Set(allowedCountries.map((code) => code.toUpperCase()));
    return all.filter((country) => allowedSet.has(country.value));
};
/** One list per language: naming and collating ~250 regions is not free. */
const countryDataByLocale = new Map();
const getCountryData = (locale) => {
    const cached = countryDataByLocale.get(locale);
    if (cached)
        return cached;
    const data = buildCountryData(locale);
    countryDataByLocale.set(locale, data);
    return data;
};
const buildCountryData = (locale) => {
    const allCountries = getCountries();
    const countries = allCountries.map((code) => ({
        value: code,
        label: getCountryName(code, locale),
    }));
    // Sort countries: priority countries first, then alphabetically. The sort
    // follows the labels the reader actually sees — sorting localized names by
    // English collation would leave the list in no visible order at all.
    const priorityCountriesData = PRIORITY_COUNTRIES.map((code) => countries.find((c) => c.value === code)).filter((c) => c !== undefined);
    const otherCountries = countries
        .filter((c) => !PRIORITY_COUNTRIES.includes(c.value))
        .sort((a, b) => {
        const first = alphabeticKey(a.label);
        const second = alphabeticKey(b.label);
        if (first !== second)
            return first < second ? -1 : 1;
        return a.value < b.value ? -1 : a.value > b.value ? 1 : 0;
    });
    return [...priorityCountriesData, ...otherCountries];
};
/** Accent-insensitive alphabetical keys without runtime-dependent collation. */
const alphabeticKey = (label) => label.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase();
/**
 * The creator space's field, as the theme's input size `creator` draws it.
 * The frame is the control (it carries the border); the input fills it.
 */
const CREATOR_FRAME = {
    h: { base: "48px", md: "44px" },
    rounded: { base: "control", md: "lg" },
};
const CREATOR_INPUT = {
    h: "full",
    rounded: { base: "control", md: "lg" },
    fontSize: { base: "15px", md: "14.5px" },
};
export const CountrySelect = forwardRef(({ name, placeholder = "Select country", value, defaultValue, onValueChange, size = "md", variant = "outline", disabled = false, required = false, width = "100%", colorPalette = "gray", zIndex = 10001, portalled = true, allowedCountries, locale = DEFAULT_COUNTRY_LOCALE, emptyText = "No countries found", ...props }, ref) => {
    const countries = useMemo(() => getCountryOptions(locale, allowedCountries), [allowedCountries, locale]);
    const { contains } = useFilter({ sensitivity: "base" });
    // Create a ref for document.body to ensure Portal renders outside Dialog
    const bodyRef = React.useRef(null);
    React.useEffect(() => {
        if (typeof document !== "undefined") {
            bodyRef.current = document.body;
        }
    }, []);
    // Internal state for uncontrolled mode
    const [internalValue, setInternalValue] = useState(defaultValue || "");
    // Determine if component is controlled
    const isControlled = value !== undefined;
    // Current selected value (controlled or uncontrolled)
    const currentValue = isControlled ? value : internalValue;
    // Find selected country
    const selectedCountry = useMemo(() => countries.find((country) => country.value === currentValue), [countries, currentValue]);
    const { collection, filter, set } = useListCollection({
        initialItems: countries,
        itemToString: (item) => item.label,
        itemToValue: (item) => item.value,
        filter: contains,
    });
    // `initialItems` only seeds the collection, so a later `locale` (or
    // `allowedCountries`) change would rename nothing: the reader switches the
    // page to French and every row of this menu stays in English, sorted and
    // searched in English too. Push the rebuilt list in whenever it changes.
    const seededCountries = useRef(countries);
    useEffect(() => {
        if (seededCountries.current === countries)
            return;
        seededCountries.current = countries;
        set([...countries]);
    }, [countries, set]);
    const handleValueChange = (details) => {
        const selectedValue = details.value?.[0] || "";
        // Update internal state if uncontrolled
        if (!isControlled) {
            setInternalValue(selectedValue);
        }
        // Notify parent component
        onValueChange?.(selectedValue);
    };
    return (_jsxs(Combobox.Root, { ref: ref, collection: collection, selectionBehavior: "clear", onInputValueChange: (details) => filter(details.inputValue), onValueChange: handleValueChange, openOnClick: true, multiple: false, size: size === "creator" ? "lg" : size, variant: variant, disabled: disabled, required: required, width: width, colorPalette: colorPalette, 
        // Opens where it fits: below when there is room, above otherwise, and never
        // taller or wider than the space left on that side (a phone with its
        // keyboard up has little of either).
        positioning: {
            strategy: "fixed",
            placement: "bottom-start",
            fitViewport: true,
        }, ...props, children: [_jsxs(Combobox.Control, { ...(size === "creator" ? CREATOR_FRAME : {}), children: [name && _jsx("input", { type: "hidden", name: name, value: currentValue }), _jsx(Combobox.Input, { ...(size === "creator" ? CREATOR_INPUT : {}), placeholder: selectedCountry ? selectedCountry.label : placeholder, style: {
                            textIndent: selectedCountry ? "2.25rem" : undefined,
                        }, css: selectedCountry
                            ? {
                                "&::placeholder": { color: "var(--chakra-colors-fg)" },
                            }
                            : undefined }), selectedCountry && (_jsx(Box, { position: "absolute", left: "3", top: "50%", transform: "translateY(-50%)", pointerEvents: "none", display: "flex", alignItems: "center", justifyContent: "center", w: "5", children: _jsx(ReactCountryFlag, { countryCode: selectedCountry.value, svg: true, style: {
                                width: "1.2em",
                                height: "1.2em",
                                display: "block",
                            }, "aria-label": selectedCountry.label }) })), _jsx(Combobox.IndicatorGroup, { children: _jsx(Combobox.Trigger, { "aria-label": "Open country list" }) })] }), _jsx(Portal, { container: bodyRef, disabled: !portalled, children: _jsx(Combobox.Positioner, { style: {
                        zIndex: zIndex,
                    }, children: _jsxs(Combobox.Content, { minW: COUNTRY_LIST_MIN_WIDTH, maxH: COUNTRY_LIST_MAX_HEIGHT, overflowY: "auto", children: [_jsx(Combobox.Empty, { children: emptyText }), collection.items.map((item) => (_jsxs(Combobox.Item, { item: item, children: [_jsx(Combobox.ItemText, { children: _jsxs(HStack, { gap: 3, children: [_jsx(ReactCountryFlag, { countryCode: item.value, svg: true, style: { width: "1.25em", height: "1.25em" } }), _jsx(Text, { children: item.label }), _jsxs(Text, { fontSize: "sm", color: "fg.muted", children: ["(", item.value, ")"] })] }) }), _jsx(Combobox.ItemIndicator, {})] }, item.value)))] }) }) })] }));
});
CountrySelect.displayName = "CountrySelect";
